// React & Helper Libraries
import { memo, useEffect, useState } from "react";
import { useTranslate } from "helpers";

// Services
import { DataWithPagination, showNotification } from "services/api";
import { getVehiclesAPI } from "services/api/catalog/vehicles/api";
import { createVehicleGroupVehiclesAPI } from "services/api/calculations/vehiclegroup/api";
import { VehicleGroupVehiclesEditFormValues } from "services/api/calculations/vehiclegroup/types";
import { initialVehicleGroupVehiclesEditFormValues } from "services/api/calculations/vehiclegroup/consts";
import { useHandleDropdownChange } from "services/forms/hooks";
import { initialDataWithPagination } from "services/api/consts";

// UI Components
import { Button } from "ui/components/Button/Button";
import { NoResults } from "ui/components/NoResults";
import { TableSearch } from "ui/components/Table/TableSearch";
import { Pagination } from "services/search";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";
import VehicleManufacturerDropdown from "ui/components/Dropdown/VehicleManufacturerDropdown/VehicleManufacturerDropdown";
import VehicleModelDropdown from "ui/components/Dropdown/VehicleModelDropdown/VehicleModelDropdown";
import VehicleTypeDropdown from "ui/components/Dropdown/VehicleTypeDropdown/VehicleTypeDropdown";

// Constants
import { CALCULATION_VEHICLE_GROUP_VEHICLE_SEARCH_COLUMNS } from "./consts";
import { VehicleGroupVehiclesEditProps } from "./types";
import { VehicleRow } from "services/api/catalog/vehicles/types";

const VehicleGroupVehiclesEditCard: React.FC<VehicleGroupVehiclesEditProps> = (
  props
) => {
  const { vehicleGroupId, onSuccess } = props;

  const { t } = useTranslate();

  const [formValues, setFormValues] =
    useState<VehicleGroupVehiclesEditFormValues>(
      initialVehicleGroupVehiclesEditFormValues
    );

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<VehicleRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (pagination: Pagination) => {
    setIsLoading(true);
    try {
      const results = await getVehiclesAPI(
        pagination,
        null,
        formValues.vehicleManufacturerId,
        formValues.vehicleModelId,
        formValues.vehicleTypeId
      );

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleSearchSubmit = async (e: any) => {
    e.preventDefault();

    await fetchSearchResult(initialDataWithPagination.pagination);
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    if (selectedRows.length === 0) {
      showNotification(
        t("admin.calculation.vehiclegroup.vehicles.add.validation"),
        { variant: "warning" }
      );
      return;
    }

    setIsLoading(true);

    try {
      let result = await createVehicleGroupVehiclesAPI(
        vehicleGroupId,
        selectedRows
      );

      if (result && onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues
  );

  return (
    <>
      <div className=" border border-slate-200 bg-slate-100 rounded-md mx-4 mb-4 h-full">
        {!isLoading && searchResults.pagination.totalPages === 0 ? (
          <NoResults
            message={t(
              "admin.calculation.setting.vehiclegroup.vehicles.search.noresults"
            )}
          />
        ) : (
          <div className="mx-[-2px] mb-[-1px]">
            <div className="p-2">
              <div className="flex gap-4">
                <div className="w-80">
                  <VehicleManufacturerDropdown
                    placeholder={t("admin.vehicle.manufacturer.placeholder")}
                    name="vehicleManufacturerId"
                    value={formValues.vehicleManufacturerId}
                    onChange={handleDropdownChange}
                    required={true}
                  />
                </div>
                <div className="w-80">
                  <VehicleModelDropdown
                    vehicleManufacturerId={formValues.vehicleManufacturerId}
                    placeholder={t("admin.vehicle.model.placeholder")}
                    name="vehicleModelId"
                    value={formValues.vehicleModelId}
                    onChange={handleDropdownChange}
                    disabled={!formValues.vehicleManufacturerId}
                    required={true}
                  />
                </div>
                <div className="w-80">
                  <VehicleTypeDropdown
                    vehicleManufacturerId={formValues.vehicleManufacturerId}
                    vehicleModelId={formValues.vehicleModelId}
                    placeholder={t("admin.vehicle.type.placeholder")}
                    name="vehicleTypeId"
                    value={formValues.vehicleTypeId}
                    onChange={handleDropdownChange}
                    disabled={!formValues.vehicleModelId}
                    required={true}
                  />
                </div>
                <div className="w-40 ">
                  <Button
                    variant="primary"
                    size="full"
                    className="text-center flex justify-center text-sm font-medium"
                    onClick={handleSearchSubmit}
                  >
                    {t("admin.search")}
                  </Button>
                </div>
              </div>
            </div>
            <TableSearch
              columns={CALCULATION_VEHICLE_GROUP_VEHICLE_SEARCH_COLUMNS}
              data={searchResults.data}
              selectedRows={selectedRows}
              pagination={
                searchResults.pagination || initialDataWithPagination.pagination
              }
              selectableRows={true}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>
      <div className="px-4 pb-5 flex justify-end ">
        <Button
          variant="primary"
          size="full"
          children={t("admin.save")}
          onClick={handleFormSubmit}
        />
      </div>
    </>
  );
};
export default memo(VehicleGroupVehiclesEditCard);
